'use client';

import { useRef, type ReactElement } from 'react';
import { cn } from '@fshr/shared-react';
import { useTranslations } from '@fshr/context/i18n-context';
import { imgOpt } from '@fshr/shared';
import { useTealium } from '@fshr/context/tealium-context';
import { experienceAvailabilityDetail } from '../plp-analytics';
import Image from '../image';
import CategoryChip from './category-chip';
import AvailabilityIndicator from './availability-indicator';
import { shouldShowAvailability } from './availability';
import ExperiencePrice from './experience-price';
import ExperienceDuration from './experience-duration';
import BevSafeText from '../bev-safe-text';
import type { CurrencySource, ProductAvailabilitySource } from './live-product-data';
import type { AvailabilityContext, ExperienceProductCardData } from './types';

type Props = {
	card: ExperienceProductCardData;
	productAvailabilityStore?: ProductAvailabilitySource;
	userStore?: CurrencySource;
	availabilityContext?: AvailabilityContext;
	interactionType?: string;
	// The category tag is opt-in: off by default, even when the card carries a category.
	showCategory?: boolean;
	className?: string;
};

export default function ExperienceProductCard({
	card,
	productAvailabilityStore,
	userStore,
	availabilityContext,
	interactionType,
	showCategory = false,
	className,
}: Props): ReactElement {
	const t = useTranslations();
	const videoRef = useRef<HTMLVideoElement>(null);
	const { trackLink } = useTealium();
	const showAvailability =
		Boolean(card.availability) && shouldShowAvailability(availabilityContext);
	const posterSrc = card.image?.defaultImage?.path
		? imgOpt({ path: card.image.defaultImage.path, cropping: card.image.defaultImage.cropping })
		: undefined;

	const playVideo = () => {
		const video = videoRef.current;
		if (!video) return;
		if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
		void video.play().catch(() => undefined);
	};

	const pauseVideo = () => {
		const video = videoRef.current;
		if (!video) return;
		video.pause();
		video.currentTime = 0;
	};

	return (
		<article
			className={cn(
				'group/card relative flex w-full flex-col gap-y-stack-m md:gap-y-stack-s',
				className
			)}
			onMouseEnter={playVideo}
			onMouseLeave={pauseVideo}
		>
			<div className="relative aspect-square w-full overflow-hidden" data-carousel-media>
				<Image
					roughSize="m"
					imagePrimitive={card.image}
					className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 ease-out motion-reduce:transition-none group-hover/card:scale-110"
				/>

				{card.videoUrl && (
					<video
						ref={videoRef}
						className="absolute inset-0 h-full w-full object-cover object-center opacity-0 transition-opacity duration-300 group-hover/card:opacity-100 md:block"
						poster={posterSrc}
						muted
						loop
						playsInline
						preload="none"
						aria-hidden="true"
					>
						<source src={card.videoUrl} type="video/mp4" />
					</video>
				)}

				{/* Figma's "tag" instance: padding is 8/4 mobile, 10/6 desktop -
				    exact match for tag-padding-x/tag-padding-y. */}
				{card.urgencyTag && (
					<span className="absolute left-060 top-060 bg-surface-translucent backdrop-blur-[15px] px-tag-padding-x py-tag-padding-y text-label text-text-primary">
						{card.urgencyTag}
					</span>
				)}

				{showAvailability && card.availability && (
					<AvailabilityIndicator
						state={card.availability.state}
						className="absolute inset-x-0 bottom-0"
					/>
				)}
			</div>

			<div className="flex flex-col gap-y-stack-2xs">
				<h3 className="text-h3 line-clamp-1 text-text-heading">
					<a
						href={card.href}
						target={card.newWindow ? '_blank' : undefined}
						rel={card.newWindow ? 'noopener' : undefined}
						onClick={() => {
							if (!interactionType) return;
							trackLink('interaction', {
								interaction_type: interactionType,
								interaction_name: 'experience card click',
								interaction_detail: experienceAvailabilityDetail(
									card.availability?.state,
									showAvailability
								),
							});
						}}
						className="before:absolute before:inset-0 before:z-0 focus-visible:outline-2 focus-visible:outline-offset-2"
					>
						<BevSafeText>{card.title}</BevSafeText>
					</a>
				</h3>

				<div className="flex flex-col gap-y-stack-xs">
					<ExperienceDuration
						productId={card.productId}
						productAvailabilityStore={productAvailabilityStore}
					/>
					<ExperiencePrice
						productId={card.productId}
						productAvailabilityStore={productAvailabilityStore}
						userStore={userStore}
						complimentaryLabel={t('Complimentary options available')}
					/>

					{showCategory && card.category && (
						<CategoryChip icon={card.category.icon} label={card.category.label} />
					)}
				</div>
			</div>
		</article>
	);
}
